import { formatExpenseMoney, expenseValidationText } from './expensePresentation';
import { formatDateTimeValue } from '../../i18n/travelFormat';
import { useTravelText } from '../../app/TravelLocaleContext';
import { useEffect, useState } from 'react';
import { minorString } from './expenseCoreModel';

/** 卡片只呈現伺服器建議；所有金額預覽以 minor-unit 整數計算。 */
export function paymentPreview(outstanding, amount, scale) {
  const normalized = minorString(amount, scale),
    due = minorString(outstanding, scale);
  const units = x => window.BigInt(x.replace('.', ''));
  const paid = units(normalized),
    total = units(due);
  if (paid <= 0n || paid > total) throw new Error("travel.expense.s06.paymentMustBePositiveAndCannotExceed");
  const remaining = total - paid,
    power = 10n ** window.BigInt(scale);
  return {
    amount: normalized,
    remaining: scale ? `${remaining / power}.${String(remaining % power).padStart(scale, '0')}` : String(remaining),
    progress: Number(paid * 100n / total)
  };
}
export default function SettlementCards({
  closing,
  scale,
  label,
  busy,
  unknown,
  onRecord
}) {
  const {t,tf,intlLocale}=useTravelText();
  const money=(value,currency)=>formatExpenseMoney(value,currency,intlLocale);
  const time=value=>formatDateTimeValue(value,intlLocale)||t('travel.expense.s06.pending');
  const [selection, setSelection] = useState(null),
    [amount, setAmount] = useState(''),
    [paidAt, setPaidAt] = useState(''),
    [review, setReview] = useState(false),
    [actual, setActual] = useState(false),
    [error, setError] = useState('');
  useEffect(() => {
    setSelection(null);
    setReview(false);
    setActual(false);
    setError('');
  }, [closing.budgetRowVersion, closing.basisHash]);
  const pick = (suggestion, full) => {
    setSelection(suggestion);
    setAmount(full ? suggestion.amount : '');
    setReview(false);
    setActual(false);
    setError('');
  };
  let preview = null;
  try {
    if (selection && amount) preview = paymentPreview(selection.amount, amount, scale);
  } catch (e) {/* 輸入中只顯示明確錯誤，不產生付款。 */}
  const disabled = busy || unknown || closing.reconfirmRequired || !closing.canEdit;
  const showReview = e => {
    e.preventDefault();
    try {
      paymentPreview(selection.amount, amount, scale);
      setError('');
      setReview(true);
    } catch (err) {
      setError(expenseValidationText(err,t,tf));
    }
  };
  const submit = () => {
    if (disabled || !review || !actual || !paidAt || !preview) return;
    onRecord({
      payerTravelerId: selection.payerTravelerId,
      payeeTravelerId: selection.payeeTravelerId,
      amount: preview.amount,
      paidAtUtc: paidAt.length === 16 ? `${paidAt}:00` : paidAt,
      actualPaymentConfirmed: true
    });
  };
  const settled = closing.balances.length > 0 && closing.balances.every(x => /^[-+]?0(?:\.0+)?$/.test(x.remaining));
  return <div className="settlement-cards" lang={intlLocale}><p>{t("travel.expense.s06.tripRepaymentSuggestionsUseConfirmedExpensesAnd")}</p>
    {settled && !unknown && !busy && !closing.reconfirmRequired && <p className={closing.settlements.length?'settlement-complete':''} role="status">{closing.settlements.length>0&&<span aria-hidden="true">✓</span>}{t(closing.settlements.length?'travel.expense.s06.currentlySettled':'travel.expense.s06.noOutstanding')}</p>}{!settled&&closing.suggestions.length===0&&!unknown&&!busy&&<p>{t('travel.expense.s06.noSuggestions')}</p>}
    <div className="settlement-card-grid">{closing.suggestions.map(s => <article className="settlement-card" key={`${s.payerTravelerId}-${s.payeeTravelerId}`}>
      <div className="settlement-flow"><strong>{label(s.payerTravelerId)}</strong><span className="settlement-arrow" aria-label={t("travel.expense.s06.pays")}>→</span><strong>{label(s.payeeTravelerId)}</strong></div>
      <p className="settlement-amount">{tf('travel.expense.s06.amountDue',{amount:money(s.amount,closing.currency)})}</p>
      {closing.canEdit && <div className="settlement-actions"><button disabled={disabled} onClick={() => pick(s, false)}>{t("travel.expense.s06.partialRepayment")}</button><button disabled={disabled} onClick={() => pick(s, true)}>{t("travel.expense.s06.settleInFull")}</button></div>}
      <details><summary>{t("travel.expense.s06.viewRepaymentHistory")}</summary>{closing.settlements.filter(f => f.payerTravelerId === s.payerTravelerId && f.payeeTravelerId === s.payeeTravelerId).map(f => <p key={f.id}>{tf('travel.expense.s06.paymentHistoryLine',{amount:money(f.amount,f.currency),time:time(f.paidAtUtc),revision:f.basisHash!==closing.basisHash?t('travel.expense.s06.paymentBeforeRevisionPreserved'):''})}</p>)}<p>{t("travel.expense.s06.historicalPaymentsAreNeverRewrittenByRevisions")}</p></details>
    </article>)}</div>
    {selection && <form className="settlement-payment" onSubmit={showReview}><fieldset disabled={disabled}><legend>{tf('travel.expense.s06.flow',{payer:label(selection.payerTravelerId),payee:label(selection.payeeTravelerId)})}</legend>
      <p>{tf('travel.expense.s06.currentlyOutstanding',{amount:money(selection.amount,closing.currency)})}</p>
      <label>{t("travel.expense.s06.thisRepaymentAmount")}<input inputMode="decimal" required value={amount} disabled={review} onChange={e => setAmount(e.target.value)} /></label>
      {preview && <><p>{tf('travel.expense.s06.repaymentPreview',{amount:money(preview.amount,closing.currency),remaining:money(preview.remaining,closing.currency)})}</p><progress aria-label={t("travel.expense.s06.expectedSettlementPercentage")} value={preview.progress} max="100" /></>}
      <label>{t("travel.expense.s06.actualPaymentTimeUtc")}<input type="datetime-local" required value={paidAt} disabled={review} onChange={e => setPaidAt(e.target.value)} /></label>
      {!review ? <button>{t("travel.expense.s06.reviewRepayment")}</button> : <div className="settlement-review"><p>{t("travel.expense.s06.confirmThePeopleAmountAndTimeThis")}</p><label><input type="checkbox" checked={actual} onChange={e => setActual(e.target.checked)} />{t("travel.expense.s06.iConfirmThisPaymentHasActuallyOccurred")}</label><button type="button" disabled={!actual || !preview} onClick={submit}>{t("travel.expense.s06.recordCompletedPayment")}</button><button type="button" onClick={() => {
            setReview(false);
            setActual(false);
          }}>{t("travel.expense.s06.backToEdit")}</button></div>}
      <button type="button" onClick={() => {
          setSelection(null);
          setReview(false);
        }}>{t("travel.expense.s06.cancel")}</button>
    </fieldset>{error && <p role="alert">{error}</p>}</form>}
  </div>;
}
